
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { createProvider } from 'react-redux';
import { IItemCacheConfiguration, IStore, createItemCacheStore } from '../../store/createStore';
import { IItemCacheState } from '../../store/reducers/itemCacheReducer';
import {
  BackgroundTaskContextProvider
} from '../backgroundTaskRunner/BackgroundTaskHost';
import { backgroundTaskQueueFacet } from '../../facets/ConfigurationFacets';
import { IBackgroundTaskQueue, IBackgroundTask } from '../../models/backgroundTasks/IBackgroundTaskQueue';
import { readConfiguration } from '../../operations/configuration/readConfiguration';

/**
 * @public
 */
export interface IItemCacheProviderProps {
  configuration?: IItemCacheConfiguration;
  store?: IStore<IItemCacheState>;
}

/**
 * @internal
 */
export const ITEM_CACHE_STORE_KEY = 'itemCacheStore';

const Provider = createProvider(ITEM_CACHE_STORE_KEY);

const ITEM_CACHE_STORE_CONTEXT_TYPES: PropTypes.ValidationMap<IItemCacheProviderContext> = {
  itemCacheStore: PropTypes.object
};

/**
 * @internal
 */
export interface IItemCacheProviderContext {
  itemCacheStore?: IStore<IItemCacheState>;
}

/**
 * Component which provides a context containing an item cache.
 * The item cache can be consumed in child components by using the `connectToItemCache` function.
 * @public
 */
export class ItemCacheProvider extends React.PureComponent<IItemCacheProviderProps, {}> {
  public static readonly contextTypes: PropTypes.ValidationMap<IItemCacheProviderContext> = {
    ...ITEM_CACHE_STORE_CONTEXT_TYPES
  };

  public context!: IItemCacheProviderContext;

  private _store: IStore<IItemCacheState>;
  private _contextStore: {} | undefined;
  private _ownStore: {
    dispose(): void;
  } | undefined;

  private _headlessBackgroundTaskQueue?: IBackgroundTaskQueue;

  constructor(props: IItemCacheProviderProps, context: IItemCacheProviderContext) {
    super(props, context);

    const {
      store: propsStore,
      configuration
    } = props;

    if (propsStore && configuration) {
      throw new Error(`Cannot initialize an ItemCacheProvider with both 'store' and 'configuration' props.`);
    }

    const {
      itemCacheStore: contextStore
    } = this.context;

    const store = this._store = propsStore || (this._contextStore = contextStore) || (this._ownStore = createItemCacheStore({
      configuration
    }));

    // The the store was not absorbed from context, then the headless background task host needs to be wrapped around
    // everything, if provided.
    this._headlessBackgroundTaskQueue = store !== contextStore ? getHeadlessBackgroundTaskQueue(store) : undefined;
  }

  public render(): JSX.Element {
    const {
      children
    } = this.props;

    const store = this._store;
    const headlessBackgroundTaskQueue = this._headlessBackgroundTaskQueue;

    return store !== this._contextStore ? (
      // If the store does not match the one currently available in React context,
      // then this provider needs to provide the hosts for background tasks, as well as the Redux store.
      // The headless queue will either be supplied in-line or routed to a queue from configuration.
      <Provider store={ store }>
        <BackgroundTaskContextProvider
          scope='headless'
          queue={ headlessBackgroundTaskQueue }
          onQueueTask={ headlessBackgroundTaskQueue ? undefined : wrapWithGlobalTaskProvider }
        >
          <BackgroundTaskContextProvider scope='global'>
            { children }
          </BackgroundTaskContextProvider>
        </BackgroundTaskContextProvider>
      </Provider>
    ) : (
        // If the store is already in context, then a parent ItemCacheProvider is providing everything
        // and this component should be pass-through.
        <>{ children }</>
      );
  }

  public componentWillUnmount(): void {
    const ownStore = this._ownStore;

    if (ownStore) {
      ownStore.dispose();
    }
  }
}

function getHeadlessBackgroundTaskQueue(store: IStore<IItemCacheState>): IBackgroundTaskQueue | undefined {
  const {
    backgroundTaskQueueFromConfiguration
  } = store.dispatch(readConfiguration({
    backgroundTaskQueueFromConfiguration: backgroundTaskQueueFacet
  }));

  return backgroundTaskQueueFromConfiguration ? {
    enqueue: (task: IBackgroundTask) => {
      backgroundTaskQueueFromConfiguration.enqueue({
        ...task,
        content: (
          <ItemCacheProvider store={ store }>
            { task.content }
          </ItemCacheProvider>
        )
      });
    }
  } : undefined;
}

function wrapWithGlobalTaskProvider(task: IBackgroundTask): IBackgroundTask {
  return {
    ...task,
    content: (
      // Headless tasks provided by this provider need to have the `global` scope added back.
      <BackgroundTaskContextProvider scope='global'>
        { task.content }
      </BackgroundTaskContextProvider>
    )
  };
}
